"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { AlertTriangle, Bell, ShoppingCart } from "lucide-react";

const ALERTS = [
  {
    icon: AlertTriangle,
    severity: { en: "Critical", es: "Crítico" },
    color: "#DC2626",
    item: "Beef chuck",
    msg: { en: "0.4 lb left. Burns out in ~12 orders.", es: "0.4 lb quedan. Se acaba en ~12 órdenes." },
    cta: { en: "Reorder now", es: "Ordenar ya" },
  },
  {
    icon: Bell,
    severity: { en: "Low", es: "Bajo" },
    color: "#FB923C",
    item: "Cilantro",
    msg: { en: "Below par-level. Time to add it to your next order.", es: "Bajo par-level. Hora de sumarlo a tu próxima orden." },
    cta: { en: "Add to purchase order", es: "Agregar a orden de compra" },
  },
  {
    icon: ShoppingCart,
    severity: { en: "Auto-86", es: "Auto-86" },
    color: "#F4722B",
    item: "Quesabirria",
    msg: { en: "Out of cheese. Menu item auto-86'd across all screens.", es: "Sin queso. Item auto-86'd en todas las pantallas." },
    cta: { en: "Manual override", es: "Override manual" },
  },
];

export default function InventoryAlertsScroll() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-[#FAFAF8] overflow-hidden">
      <div
        className="absolute top-0 left-0 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(244,114,43,0.10) 0%, transparent 65%)" }}
      />

      <div className="relative max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 max-w-[760px] mx-auto"
        >
          <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#D7263D" }}>
            {t("Low-stock alerts", "Alertas de stock bajo")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02] text-redy-charcoal">
            {t("Out of beef ", "Sin carne ")}
            <span className="text-redy-charcoal/30">{t("before the customer asks.", "antes de que el cliente pregunte.")}</span>
          </h2>
          <p className="text-[16px] font-medium text-redy-secondary leading-relaxed mt-6">
            {t(
              "Set a par-level per ingredient. When stock crosses it, the manager gets an alert. When stock hits zero, the menu item auto-86's across POS, website, kiosk and menu board. No more \"sorry we're out of...\" surprises.",
              "Defines par-level por ingrediente. Cuando el stock cruza, el manager recibe una alerta. Cuando llega a cero, el item se auto-86 en POS, sitio, kiosko y menu board. Sin más sorpresas de \"sorry estamos sin...\"."
            )}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-[1080px] mx-auto">
          {ALERTS.map((alert, i) => {
            const Icon = alert.icon;
            return (
              <motion.div
                key={alert.item}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6, delay: 0.1 * i, ease: [0.21, 0.47, 0.32, 0.98] }}
                className="bg-white border border-black/5 rounded-[20px] p-6 shadow-[0_20px_60px_rgba(0,0,0,0.04)] relative overflow-hidden"
              >
                <div
                  className="absolute top-0 right-0 w-32 h-32 rounded-full pointer-events-none"
                  style={{
                    background: `radial-gradient(circle, ${alert.color}1f 0%, transparent 70%)`,
                  }}
                />

                <div className="relative">
                  <div className="flex items-center justify-between mb-5">
                    <div
                      className="w-11 h-11 rounded-[12px] flex items-center justify-center"
                      style={{ backgroundColor: `${alert.color}1f` }}
                    >
                      <Icon className="w-5 h-5" style={{ color: alert.color }} />
                    </div>
                    <div
                      className="text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-full"
                      style={{
                        backgroundColor: `${alert.color}1f`,
                        color: alert.color,
                      }}
                    >
                      {t(alert.severity.en, alert.severity.es)}
                    </div>
                  </div>

                  <div className="text-[20px] font-black text-redy-charcoal mb-2 tracking-tight">
                    {alert.item}
                  </div>
                  <div className="text-[13px] font-medium text-redy-secondary leading-relaxed mb-5">
                    {t(alert.msg.en, alert.msg.es)}
                  </div>

                  <div
                    className="text-[11px] font-black uppercase tracking-wider px-3 py-2 rounded-full text-center"
                    style={{
                      backgroundColor: `${alert.color}10`,
                      color: alert.color,
                    }}
                  >
                    {t(alert.cta.en, alert.cta.es)} →
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
